import { t } from "@lingui/macro";
import { useState } from "react";
import {
  ArrowCounterClockwiseIcon,
  CircleNotchIcon,
  DownloadSimpleIcon,
  EnvelopeSimpleIcon,
  MegaphoneIcon,
  TrashIcon,
  UserPlusIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { toast } from "sonner";

import { KpiCard } from "../_components/kpi-card";
import { Column, DataTable } from "../_components/datatables";
import { ImportWizard } from "./_components/import-wizard";
import {
  exportProvisionBatch,
  useDeleteProvisionBatch,
  useProvisionBatchRows,
  useProvisionBatches,
  useResendInvites,
  type ProvisionBatch,
} from "@/client/services/campaigns/provisioning";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  PAGE ADMIN « IMPORTS & ACTIVATION » (/admin/dashboard/imports)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Migration d'audience : CSV d'anciens utilisateurs → comptes provisionnés →
 *  invitations d'activation. Suivi live (socket `provisioning:update`) de
 *  l'entonnoir importés → invités → réclamés. Relances + export CSV.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const STATUS_META: Record<string, { label: string; className: string }> = {
  processing: { label: "Traitement", className: "bg-sky-500/10 text-sky-600 " },
  ready: { label: "Prêt", className: "bg-slate-500/10 text-slate-600 " },
  inviting: {
    label: "Invitations en cours",
    className: "bg-amber-500/10 text-amber-600 ",
  },
  done: { label: "Terminé", className: "bg-emerald-500/10 text-emerald-600 " },
  failed: { label: "Échec", className: "bg-red-500/10 text-red-600 " },
};

const fmtDateTime = (iso?: string | null) =>
  iso
    ? new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }).format(
        new Date(iso),
      )
    : "—";

const BatchDetailDialog = ({ batchId, onClose }: { batchId: string; onClose: () => void }) => {
  const [claimed, setClaimed] = useState<"all" | "claimed" | "unclaimed">("all");
  const [search, setSearch] = useState("");
  const { batch, rows, loading } = useProvisionBatchRows(batchId, {
    claimed,
    search: search || undefined,
  });
  const resend = useResendInvites();
  const remove = useDeleteProvisionBatch();

  const doResend = async () => {
    try {
      const { queued } = await resend.mutateAsync({ batchId });
      toast.success(`${queued} invitation(s) remise(s) en file`);
    } catch {
      toast.error("Échec de la relance des invitations");
    }
  };

  const doExport = async () => {
    try {
      await exportProvisionBatch(batchId);
    } catch {
      toast.error("Échec de l'export");
    }
  };

  const doDelete = async () => {
    try {
      await remove.mutateAsync(batchId);
      toast.success("Lot supprimé — les comptes sont conservés");
      onClose();
    } catch {
      toast.error("Échec de la suppression du lot");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-admin-border bg-admin-surface">
        <DialogHeader>
          <DialogTitle className="text-admin-text-primary">
            {batch?.name ?? "Lot d'import"}
          </DialogTitle>
          <DialogDescription className="text-admin-text-muted">
            {batch?.createdCount.toLocaleString("fr-FR")} compte(s) provisionné(s) ·{" "}
            {batch?.invitedCount.toLocaleString("fr-FR")} invitation(s) envoyée(s) ·{" "}
            {batch?.claimedCount.toLocaleString("fr-FR")} réclamé(s) ·{" "}
            {batch?.failedCount.toLocaleString("fr-FR")} échec(s) d'envoi
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={claimed} onValueChange={(v) => setClaimed(v as typeof claimed)}>
            <SelectTrigger className="w-48 border-admin-border bg-admin-surface-elevated">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les comptes</SelectItem>
              <SelectItem value="claimed">Réclamés (actifs)</SelectItem>
              <SelectItem value="unclaimed">Non réclamés</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un e-mail…"
            className="w-full max-w-xs border-admin-border bg-admin-surface-elevated"
          />
        </div>

        {loading ? (
          <p className="py-6 text-center text-admin-text-muted">Chargement…</p>
        ) : (
          <div className="max-h-96 divide-y divide-admin-border overflow-y-auto rounded-lg border border-admin-border">
            {rows.length === 0 && (
              <p className="py-6 text-center text-sm text-admin-text-muted">Aucun compte.</p>
            )}
            {rows.map((row) => (
              <div key={row.userId} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm text-admin-text-primary">
                    {row.name ?? row.email}
                  </p>
                  <p className="truncate text-xs text-admin-text-muted">{row.email}</p>
                </div>
                <div className="shrink-0 text-right">
                  {row.invitationAcceptedAt ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                      Actif · {fmtDateTime(row.invitationAcceptedAt)}
                    </span>
                  ) : row.invitationSendError ? (
                    <span
                      className="inline-flex max-w-44 items-center gap-1 truncate text-xs text-red-600"
                      title={row.invitationSendError}
                    >
                      Échec envoi
                    </span>
                  ) : row.invitationSentAt ? (
                    <span className="inline-flex items-center gap-1 text-xs text-sky-600">
                      Invité · expire {fmtDateTime(row.invitationExpiresAt)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-admin-text-muted">
                      En file
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-admin-border pt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={doResend}
            disabled={resend.isPending || batch?.status === "processing"}
            className="gap-1.5"
          >
            {resend.isPending ? (
              <CircleNotchIcon size={14} className="animate-spin" />
            ) : (
              <ArrowCounterClockwiseIcon size={14} />
            )}
            Relancer les invitations
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={doExport} className="gap-1.5">
              <DownloadSimpleIcon size={14} /> Exporter
            </Button>
            <Button
              variant="error"
              size="icon"
              onClick={doDelete}
              disabled={remove.isPending}
              title="Supprimer le lot"
            >
              <TrashIcon size={14} />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ImportsAdminPage = () => {
  const { batches, loading } = useProvisionBatches();
  const [detailId, setDetailId] = useState<string | null>(null);

  const stats = batches.reduce(
    (acc, batch) => ({
      created: acc.created + batch.createdCount,
      invited: acc.invited + batch.invitedCount,
      claimed: acc.claimed + batch.claimedCount,
    }),
    { created: 0, invited: 0, claimed: 0 },
  );

  const columns: Column<ProvisionBatch>[] = [
    {
      key: "name",
      header: "Lot",
      render: (b) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-admin-text-primary">{b.name}</p>
          {b.fileName && <p className="truncate text-xs text-admin-text-muted">{b.fileName}</p>}
        </div>
      ),
    },
    {
      key: "status",
      header: "Statut",
      render: (b) => {
        const meta = STATUS_META[b.status] ?? STATUS_META.ready;
        return <Badge className={meta.className}>{meta.label}</Badge>;
      },
    },
    {
      key: "createdCount",
      id: "funnel",
      header: "Importés → Invités → Réclamés",
      render: (b) => (
        <span className="text-sm text-admin-text-secondary">
          {b.createdCount.toLocaleString("fr-FR")} → {b.invitedCount.toLocaleString("fr-FR")} →{" "}
          <strong className="text-emerald-600">{b.claimedCount.toLocaleString("fr-FR")}</strong>
          {b.existingCount > 0 && (
            <span className="text-xs text-admin-text-muted">
              {" "}
              (+{b.existingCount.toLocaleString("fr-FR")} déjà là)
            </span>
          )}
        </span>
      ),
    },
    {
      key: "failedCount",
      id: "errors",
      header: "Anomalies",
      render: (b) =>
        b.invalidCount > 0 || b.failedCount > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs text-amber-600">
            <UsersIcon size={12} /> {b.invalidCount} invalides · {b.failedCount} échecs
          </span>
        ) : (
          <span className="text-admin-text-muted">—</span>
        ),
    },
    {
      key: "createdAt",
      header: "Créé le",
      align: "right",
      render: (b) => <span className="text-sm">{fmtDateTime(b.createdAt)}</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-admin-text-primary">Imports & Activation</h1>
        <p className="text-sm text-admin-text-muted">
          Migrez votre ancienne base d'utilisateurs : comptes provisionnés + invitations
          d'activation e-mail.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={<MegaphoneIcon size={20} />}
          label="Lots d'import"
          value={batches.length}
          tone="violet"
        />
        <KpiCard
          icon={<UserPlusIcon size={20} />}
          label="Comptes provisionnés"
          value={stats.created.toLocaleString("fr-FR")}
          tone="sky"
        />
        <KpiCard
          icon={<EnvelopeSimpleIcon size={20} />}
          label="Invitations envoyées"
          value={stats.invited.toLocaleString("fr-FR")}
          tone="amber"
        />
        <KpiCard
          icon={<UsersIcon size={20} />}
          label="Comptes réclamés (actifs)"
          value={stats.claimed.toLocaleString("fr-FR")}
          tone="emerald"
        />
      </div>

      {/* Assistant d'import */}
      <ImportWizard onImported={() => undefined} />

      {/* Liste des lots */}
      <DataTable
        columns={columns}
        data={batches}
        loading={loading}
        onRowClick={(b) => setDetailId(b.id)}
        pageSize={10}
        emptyState={
          <div className="flex flex-col items-center gap-2">
            <MegaphoneIcon size={32} className="text-admin-text-muted" />
            <p>{t`Aucun lot — importez votre premier CSV (.csv) ci-dessus.`}</p>
          </div>
        }
      />

      {detailId && <BatchDetailDialog batchId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
};
